import React, {Fragment, PureComponent} from "react"
import styled from "styled-components"

import {Body, Label} from "@irati/ui/src/Typography"
import id from "@irati/utils/id"
import ErrorText from "@irati/ui/src/ErrorText"
import {Checkbox, ValidationButton} from "@irati/ui/src/FormItem"
import {Recognition} from "@irati-web/fields/poll/votes"
import CreatePollVoteMutation, {Data as VotePollRes} from "@irati/recognition/apollo/mutation/Vote/Create"
import {FormattedMessage, InjectIntl, injectIntl} from "@irati/utils/lang"
import tracking from "@irati-tracking/recognition/poll"
import formatRelativeForOpenPolls from "@irati/recognition/utils/formatRelativeForOpenPolls"
import CompanyValues from "@irati-web/companyvalues/src/components/CompanyValuesList"
import {Poll, User} from "@irati/types"

import UserSearch from "@irati/ui/src/UserSearch"
import {MAX_CHAR_LENGTH} from "@irati-web/fields/poll/votes/validation/comment"
import {commentSchema} from "@irati-web/fields/poll/votes/validation"
import Error from "@irati/ui/src/Loading/Error"
import {CompanyValuesWrapper, Top, Bottom, TeamList, BigDesc, Form as FormBase, Heading} from "../shared/layouts"
import Actions from "./Actions"
import {VIEW} from "./Popup"
enum ErrorCodes {
  PBRecognitionAppCreateVoteFailed = "PBRecognitionAppCreateVoteFailed"
}
const ErrorCodesMapping = {
  [ErrorCodes.PBRecognitionAppCreateVoteFailed]: "recognition.polls.msg.voteOverTime"
}
const Form = styled(FormBase)`
  margin-bottom: 0;
`

const EndDate = styled(Label)`
  color: ${({theme}) => theme.colors.greys.medium};
`

interface Props extends InjectIntl {
  poll: Poll
  onDeleteSuccess: () => void
  selectScreen: (modalToShow: string | null) => void
  id?: string
}

interface State {
  voteText: string
  voteUser: User | null
  isAnonymous: boolean
}

class CreateVote extends PureComponent<Props, State> {
  static defaultProps = {
    id: "create-vote"
  }

  state: State = {
    voteText: "",
    voteUser: null,
    isAnonymous: false
  }

  handleChange = (voteText: string) => this.setState({voteText})

  handleSelectUser = (_, voteUser: User) => {
    this.setState({voteUser})
  }

  toggleAnonymous = () => {
    this.setState({isAnonymous: !this.state.isAnonymous})
  }

  handlePollVoteComplete = (res: VotePollRes["data"]) => {
    tracking.createPollVote(res.createPollVote)
    this.props.selectScreen(VIEW)
  }

  handlePollVoteError = (response: any) => {
    tracking.errorsPollVote(response)
  }

  isOverTime(apolloState) {
    const hasCodeMapped = ErrorCodesMapping[apolloState?.error?.networkError?.errorCode]
    const {poll, formatMessage} = this.props
    const isPollClosed = new Date(poll.endsAt).getTime() - new Date().getTime() < 0
    if (!hasCodeMapped || !isPollClosed) {
      return null
    }
    return formatMessage({id: hasCodeMapped})
  }

  render() {
    const {poll, intl, onDeleteSuccess} = this.props
    const {voteText, voteUser} = this.state
    const teamIds = poll.teams?.results?.map(({uuid}) => uuid) || []
    return (
      <Fragment>
        <Top className="center">
          <Actions poll={poll} onDeleteSuccess={onDeleteSuccess} />
          <TeamList teams={poll.teams.results} />
          <Heading withBody bold>
            {poll.title}
          </Heading>
          <BigDesc>{poll.desc}</BigDesc>
          <CompanyValuesWrapper>
            <CompanyValues companyValues={poll.companyValues} />
          </CompanyValuesWrapper>
          <EndDate>{formatRelativeForOpenPolls(poll.endsAt, intl)}</EndDate>
        </Top>
        <Bottom>
          <Form>
            <UserSearch
              teamIds={teamIds}
              onChange={this.handleSelectUser}
              placeholder="recognition.fields.poll.votes.placeholder"
              label="recognition.fields.poll.votes.label"
              hideLabel
              autoFocus
              removeSelf
            />
            {voteUser && (
              <CreatePollVoteMutation
                poll={poll}
                user={voteUser}
                message={voteText}
                isAnonymous={this.state.isAnonymous}
                onCompleted={this.handlePollVoteComplete}
                onError={this.handlePollVoteError}>
                {(onClick, apolloState) => (
                  <Fragment>
                    <Recognition
                      onChange={this.handleChange}
                      value={voteText}
                      maxLength={MAX_CHAR_LENGTH}
                      validation={commentSchema(intl)}
                    />
                    {poll.allowAnonymousVoting && (
                      <Checkbox checked={this.state.isAnonymous} name="isAnonymous" onChange={this.toggleAnonymous}>
                        <Body small display="inline">
                          <FormattedMessage id="recognition.popup.votes.createVote.anonymous" />
                        </Body>
                        <Body small weight="normal">
                          <FormattedMessage id="recognition.popup.votes.createVote.anonymousHint" />
                        </Body>
                      </Checkbox>
                    )}
                    <ValidationButton
                      marginBottom={2}
                      id={id("submit", this.props)}
                      onClick={onClick}
                      apolloState={apolloState}
                      full
                      primary>
                      <FormattedMessage id="recognition.popup.votes.createVote.button" />
                    </ValidationButton>
                    {apolloState.error && !this.isOverTime(apolloState) && (
                      <Error error={apolloState.error} errorMsg={apolloState.errorMsg}></Error>
                    )}
                    {this.isOverTime(apolloState) && <ErrorText>{this.isOverTime(apolloState)}</ErrorText>}
                  </Fragment>
                )}
              </CreatePollVoteMutation>
            )}
          </Form>
        </Bottom>
      </Fragment>
    )
  }
}

export default injectIntl(CreateVote)
